/**
 * This stores the UI states of MembershipContainer.
 *
 * Typically we want to make container states private through `useState`,
 * but in this container, we want the UI states to persist beyond its life cycle.
 * E.g. when user navigates to payment container then come back.
 * So we use an external store to keep these states.
 */

import { applyReducers } from '@m/effector-utils';
import { Site } from '@m/types/api';
import { Bounds, CenterCoordinate } from '@m/types/MapTypes';
import { createStore, Event } from 'effector';

import { setBounds, setCenterCoordinate, setIndividualSubscriptionInvitationId } from './actions';

type MembershipState = {
  isActiveSubsExpanded: boolean;
  isUpcomingSubsExpanded: boolean;
  selectedSite: Site | null;
  bounds: Bounds | null;
  centerCoordinate: CenterCoordinate | null;
  individualSubscriptionInvitationId: string | null;
};

const initialState: MembershipState = {
  isActiveSubsExpanded: false,
  isUpcomingSubsExpanded: false,
  selectedSite: null,
  bounds: null,
  centerCoordinate: null,
  individualSubscriptionInvitationId: null,
};
const store = createStore(initialState);

type Reducers = {
  setBounds: {
    action: Event<Bounds>;
    reducer: (state: MembershipState, bounds: Bounds) => MembershipState;
  };
  setCenterCoordinate: {
    action: Event<CenterCoordinate>;
    reducer: (state: MembershipState, centerCoord: CenterCoordinate) => MembershipState;
  };
  setIndividualSubscriptionInvitationId: {
    action: Event<string | null>;
    reducer: (state: MembershipState, payload: string | null) => MembershipState;
  };
};

export const reducers: Reducers = {
  setBounds: {
    action: setBounds,
    reducer: (state, bounds) => ({
      ...state,
      bounds,
    }),
  },
  setIndividualSubscriptionInvitationId: {
    action: setIndividualSubscriptionInvitationId,
    reducer: (state, payload) => ({
      ...state,
      individualSubscriptionInvitationId: payload,
    }),
  },
  setCenterCoordinate: {
    action: setCenterCoordinate,
    reducer: (state, centerCoordinate) => ({
      ...state,
      centerCoordinate,
    }),
  },
};

export default applyReducers({ store, reducers });
